import { useLayoutEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";
import {
  ChevronRight,
  Folder,
  FolderOpen,
  FolderPlus,
  Home,
  Pencil,
  Trash2,
} from "lucide-react";
import { useTranslationApp } from "../../lib/i18next";
import {
  GALLERY_FOLDERS_ALL,
  GALLERY_FOLDERS_BAR_ARIA_LABEL,
  GALLERY_FOLDERS_DELETE_ARIA_LABEL,
  GALLERY_FOLDERS_NEW_INLINE,
  GALLERY_FOLDERS_RENAME_ARIA_LABEL,
} from "../../i18n/keysTranslations";
import { GALLERY_DRAG_MIME_SELECTED } from "../../utils/constants";
import { getBreadcrumb, getChildFolders } from "../../utils/contentFolderUtils";

const folderBarScrollSx = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  minHeight: 36,
  gap: 0,
  overflowX: "auto",
  overflowY: "hidden",
  pt: 1.25,
  pb: 1.25,
  bgcolor: "transparent",
  scrollbarWidth: "thin",
  scrollbarColor: "#e0e0e0 transparent",
  "&::-webkit-scrollbar": {
    height: 5,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#e0e0e0",
    borderRadius: 999,
  },
};

const GalleryFolderBar = ({
  folders = [],
  activeFolderID = null,
  selectedContentIDs = [],
  canEdit = false,
  isLoading = false,
  onSelectFolder = () => {},
  onOpenCreateFolder = () => {},
  onDropMany = () => {},
  onRenameFolder = () => {},
  onDeleteFolder = null,
  onHorizontalOverflowChange = null,
  pinBottomPadding = false,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const [dragOverID, setDragOverID] = useState(undefined);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const folderScrollRef = useRef(null);

  const browseFolderID = useMemo(() => {
    const activeFolder = folders.find((folder) => folder.id === activeFolderID);
    if (activeFolder?.isSystem) return null;
    return activeFolderID;
  }, [activeFolderID, folders]);

  const breadcrumb = useMemo(
    () => getBreadcrumb({ folders, folderID: browseFolderID }),
    [browseFolderID, folders]
  );

  const visibleFolders = useMemo(
    () => getChildFolders({ folders, parentFolderID: browseFolderID }),
    [browseFolderID, folders]
  );

  useLayoutEffect(() => {
    const shouldObserve =
      pinBottomPadding || typeof onHorizontalOverflowChange === "function";
    if (!shouldObserve) return undefined;

    const element = folderScrollRef.current;
    const publish = (overflows) => {
      setHasHorizontalOverflow((current) =>
        current === overflows ? current : overflows
      );
      if (typeof onHorizontalOverflowChange === "function") {
        onHorizontalOverflowChange(overflows);
      }
    };

    if (!element) {
      publish(false);
      return undefined;
    }

    const reportOverflow = () => {
      publish(element.scrollWidth - element.clientWidth > 1);
    };

    reportOverflow();
    const observer = new ResizeObserver(reportOverflow);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);

    return () => observer.disconnect();
  }, [onHorizontalOverflowChange, pinBottomPadding, visibleFolders]);

  const selectedCount = selectedContentIDs.length;

  const resolveContentIDs = (event) => {
    const isPartOfSelection =
      event.dataTransfer.getData(GALLERY_DRAG_MIME_SELECTED) === "1";
    const draggedID = event.dataTransfer.getData("text/plain");

    if (!draggedID) return [];

    if (isPartOfSelection && selectedCount > 1) {
      return selectedContentIDs;
    }

    return [draggedID];
  };

  const onDragOver = (event, folderID) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverID(folderID);
  };

  const onDragLeaveTarget = (event) => {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
      return;
    }
    setDragOverID(undefined);
  };

  const onDrop = (event, folderID) => {
    event.preventDefault();
    setDragOverID(undefined);

    const contentIDs = resolveContentIDs(event);
    if (contentIDs.length === 0) return;

    onDropMany({ contentIDs, folderID });
  };

  const getDropCountLabel = () => (selectedCount > 1 ? selectedCount : 1);

  const getDropBadgeSx = ({ isDragTarget }) => ({
    position: "absolute",
    top: -6,
    right: -4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    px: 0.75,
    py: 0.25,
    borderRadius: 10,
    bgcolor: "primary.main",
    color: "common.white",
    fontWeight: 700,
    lineHeight: 1.2,
    pointerEvents: "none",
    opacity: isDragTarget ? 1 : 0,
    visibility: isDragTarget ? "visible" : "hidden",
    transition: "opacity 0.15s ease",
    zIndex: 1,
  });

  const getBreadcrumbItemSx = ({ isActive, isDragTarget }) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: isActive ? 1.25 : 0.5,
    py: 0.75,
    borderRadius: 3,
    fontSize: 12,
    fontWeight: isActive ? 600 : 500,
    whiteSpace: "nowrap",
    border: isActive || isDragTarget ? "1px solid" : "none",
    transition: "all 0.15s ease",
    flexShrink: 0,
    bgcolor: "transparent",
    ...(isDragTarget
      ? {
          bgcolor: "primary.50",
          borderColor: "transparent",
          color: "primary.dark",
          transform: "scale(1.03)",
          boxShadow: 2,
          outline: "none",
        }
      : isActive
        ? {
            background: theme.palette.button.primary.main,
            borderColor: "transparent",
            color: theme.palette.button.primary.contrastText,
          }
        : {
            color: "grey.600",
            "&:hover": {
              color: "grey.900",
              bgcolor: "grey.100",
            },
          }),
  });

  const getFolderChipSx = ({ isActive, isDragTarget }) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.25,
    py: 0.75,
    borderRadius: 3,
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
    border: "1px solid",
    transition: "all 0.15s ease",
    flexShrink: 0,
    ...(isDragTarget
      ? {
          bgcolor: "primary.50",
          borderColor: "transparent",
          color: "primary.dark",
          transform: "scale(1.03)",
          boxShadow: 2,
          outline: "none",
        }
      : isActive
        ? {
            background: theme.palette.button.primary.main,
            borderColor: "transparent",
            color: theme.palette.button.primary.contrastText,
          }
        : {
            bgcolor: "common.white",
            borderColor: "grey.300",
            color: "grey.800",
            "&:hover": {
              borderColor: "grey.500",
              bgcolor: "grey.50",
            },
          }),
  });

  const getFolderActionButtonSx = ({ isActive }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 0,
    minWidth: 0,
    height: 18,
    borderRadius: "50%",
    border: "none",
    bgcolor: isActive ? "rgba(255,255,255,0.2)" : "grey.100",
    color: isActive ? "inherit" : "grey.600",
    opacity: 0,
    overflow: "hidden",
    flexShrink: 0,
    ml: -0.75,
    pointerEvents: "none",
    transition:
      "width 0.15s ease, min-width 0.15s ease, opacity 0.15s ease, margin 0.15s ease",
    cursor: isLoading ? "not-allowed" : "pointer",
    "&:hover": {
      bgcolor: isActive ? "rgba(255,255,255,0.35)" : "grey.200",
      color: isActive ? "inherit" : "grey.900",
    },
  });

  const getCreateFolderButtonSx = () => ({
    width: 33,
    height: 33,
    minWidth: 33,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    p: 0,
    boxSizing: "border-box",
    borderRadius: 3,
    border: "1px dashed",
    borderColor: "grey.300",
    bgcolor: "common.white",
    color: "grey.600",
    cursor: isLoading ? "not-allowed" : "pointer",
    opacity: isLoading ? 0.5 : 1,
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: "grey.500",
      bgcolor: "grey.50",
      color: "grey.900",
    },
    "&:disabled": {
      pointerEvents: "none",
    },
  });

  return (
    <Box
      aria-label={t(GALLERY_FOLDERS_BAR_ARIA_LABEL)}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        gap: 1,
        pt: 1.25,
        pb: pinBottomPadding ? 1.25 : 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          flexShrink: 0,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onSelectFolder(null)}
          onDragOver={(event) => onDragOver(event, null)}
          onDragLeave={onDragLeaveTarget}
          onDrop={(event) => onDrop(event, null)}
          sx={{
            ...getBreadcrumbItemSx({
              isActive: activeFolderID === null,
              isDragTarget: dragOverID === null,
            }),
            cursor: "pointer",
          }}
        >
          <Home size={12} />
          <Box component="span" sx={{ mt: 0.2 }}>
            {" "}
            {t(GALLERY_FOLDERS_ALL)}
          </Box>
        </Box>

        {breadcrumb.map((folder) => {
          const isActive = activeFolderID === folder.id;

          return (
            <Box
              key={folder.id}
              sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
            >
              <ChevronRight size={12} color="#9ca3af" />
              <Box
                component="button"
                type="button"
                onClick={() => onSelectFolder(folder.id)}
                sx={{
                  ...getBreadcrumbItemSx({ isActive, isDragTarget: false }),
                  cursor: "pointer",
                }}
              >
                {folder.name}
              </Box>
            </Box>
          );
        })}
      </Box>

      {visibleFolders.length > 0 || canEdit ? (
        <Box
          sx={{
            width: "1px",
            height: 33,
            bgcolor: "grey.300",
            flexShrink: 0,
          }}
        />
      ) : null}
      {canEdit ? (
        <Box
          component="button"
          type="button"
          onClick={onOpenCreateFolder}
          disabled={isLoading}
          aria-label={t(GALLERY_FOLDERS_NEW_INLINE)}
          sx={getCreateFolderButtonSx()}
        >
          <FolderPlus size={14} />
        </Box>
      ) : null}
      <Box
        ref={folderScrollRef}
        sx={{
          ...folderBarScrollSx,
          flex: 1,
          minWidth: 0,
          width: "auto",
          pt: 0,
          pb: pinBottomPadding && !hasHorizontalOverflow ? 0 : 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {visibleFolders.map((folder) => {
            const isActive = activeFolderID === folder.id;
            const isSystemFolder = Boolean(folder.isSystem);
            const allowDrop = true;
            const allowRename = canEdit && !isSystemFolder;
            const allowDelete =
              canEdit &&
              !isSystemFolder &&
              typeof onDeleteFolder === "function";
            const isDragTarget = allowDrop && dragOverID === folder.id;
            const dropCountLabel = getDropCountLabel();

            return (
              <Box
                key={folder.id}
                onDragOver={
                  allowDrop
                    ? (event) => onDragOver(event, folder.id)
                    : undefined
                }
                onDragLeave={allowDrop ? onDragLeaveTarget : undefined}
                onDrop={
                  allowDrop ? (event) => onDrop(event, folder.id) : undefined
                }
                sx={{
                  ...getFolderChipSx({ isActive, isDragTarget }),
                  position: "relative",
                  ...(allowRename || allowDelete
                    ? {
                        "&:hover .folder-rename-btn, &:hover .folder-delete-btn":
                          {
                            width: 18,
                            minWidth: 18,
                            opacity: 1,
                            ml: 0,
                            pointerEvents: "auto",
                          },
                        "&:hover .folder-rename-btn + .folder-delete-btn": {
                          ml: -0.5,
                        },
                      }
                    : {}),
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => onSelectFolder(folder.id)}
                  aria-label={folder.name}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    flex: 1,
                    minWidth: 0,
                    p: 0,
                    m: 0,
                    border: "none",
                    bgcolor: "transparent",
                    color: "inherit",
                    font: "inherit",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {isActive ? (
                    <FolderOpen size={14} color="currentColor" />
                  ) : (
                    <Folder
                      size={14}
                      color={isDragTarget ? "#3b82f6" : "#000000"}
                    />
                  )}
                  <Box component="span" sx={{ mt: 0.2 }}>
                    {folder.name}
                  </Box>
                </Box>
                <Box
                  component="span"
                  aria-hidden={!isDragTarget}
                  sx={getDropBadgeSx({ isDragTarget })}
                >
                  +{dropCountLabel}
                </Box>
                {allowRename ? (
                  <Box
                    component="button"
                    type="button"
                    className="folder-rename-btn"
                    aria-label={t(GALLERY_FOLDERS_RENAME_ARIA_LABEL, {
                      name: folder.name,
                    })}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRenameFolder(folder);
                    }}
                    sx={getFolderActionButtonSx({ isActive })}
                  >
                    <Pencil size={10} />
                  </Box>
                ) : null}
                {allowDelete ? (
                  <Box
                    component="button"
                    type="button"
                    className="folder-delete-btn"
                    aria-label={t(GALLERY_FOLDERS_DELETE_ARIA_LABEL, {
                      name: folder.name,
                    })}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteFolder(folder);
                    }}
                    sx={getFolderActionButtonSx({ isActive })}
                  >
                    <Trash2 size={10} />
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default GalleryFolderBar;
